import React, { useContext, useEffect, useMemo, useState } from "react";
import { WalletsContext } from "../../state/context/wallets.context";
import {
    Box, Button, MenuItem, Select, TextField, InputAdornment, Stack,
    FormControl, InputLabel, Typography, ListSubheader, useTheme
} from "@mui/material";
import { API_BASE_URL } from "../../utils/constants";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../state/context/auth.context";

const QuickSell = ({ tokenId, maxAmount }) => {
    const { t } = useTranslation();
    const theme = useTheme(); // Add theme hook
    const { state: AuthState } = useContext(AuthContext);
    const { isConnected } = AuthState.user || {};

    const [selectedWallet, setSelectedWallet] = useState("");
    const [amount, setAmount] = useState(maxAmount);
    const [slippage, setSlippage] = useState(0);

    const { state, getWallets } = useContext(WalletsContext);
    const walletList = state.wallets || [];
    const walletGroups = state.walletGroups || [];

    const { mainWallet } = state;


    // Filter favorite wallets using useMemo for slight optimization
    const favoriteWallets = useMemo(() => {
        let walletsList = walletList.filter((wallet) => wallet.isFavorite);
        if (mainWallet) {
            walletsList = [...walletsList, mainWallet];
        }
        return walletsList;

    }, [walletList, mainWallet]);


    // Load cached values on component mount
    useEffect(() => {
        const cachedWallet = localStorage.getItem("selectedWallet");
        const cachedSlippage = localStorage.getItem("slippage");
        if (cachedSlippage) setSlippage(cachedSlippage);
        if (cachedWallet) setSelectedWallet(cachedWallet);
    }, []);

    // Update cache when selectedWallet changes
    const handleWalletChange = (e) => {
        const newWallet = e.target.value;
        setSelectedWallet(newWallet);
        localStorage.setItem("selectedWallet", newWallet);
    };

    // Update cache when amount changes
    const handleAmountChange = (e) => {
        const newAmount = e.target.value;
        setAmount(newAmount);
    };

    const handleSlippageChange = (e) => {
        const newSlippage = e.target.value;
        setSlippage(newSlippage);
        localStorage.setItem("slippage", newSlippage);
    };

    // Renamed function to avoid naming conflict with component
    const handleQuickSell = async () => {
        if (amount <= 0) {
            toast.error("Amount must be greater than 0");
            return;
        }
        if (amount > maxAmount) {
            toast.error("Amount exceeds max amount");
            return;
        }
        let wallets = [];

        if (mainWallet && selectedWallet === "ODIN_MAIN_WALLET") {
            wallets.push(mainWallet);
        }
        // Try to find a wallet that matches the selected wallet ID
        const selectedWalletObj = walletList.find((wallet) => wallet._id === selectedWallet);
        if (selectedWalletObj) {
            wallets.push(selectedWalletObj);
        } else {
            // Otherwise, get all wallets belonging to the selected wallet group
            wallets = walletList.filter((wallet) => wallet.group === selectedWallet);
            if (wallets.length === 0) {
                toast.error("Please select a wallet or wallet group");
                return;
            }
        }

        for (const wallet of wallets) {
            const toastId = toast.loading(`Attempting to sell: ${ tokenId }`, {
                position: "bottom-right",
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "dark",
            });

            try {
                const resp = await fetch(`${ API_BASE_URL }/token/${ tokenId }/price`);
                const price = (await resp.json()).price;
                let walletData = wallet.serializeIdentity;

                let chossenWallet = {};
                if (walletData) {
                    chossenWallet = {
                        walletData: walletData,
                        type: "BTC"
                    };
                } else {
                    chossenWallet = {
                        walletData: wallet.privateKey,
                        type: "ICP"
                    };
                }

                let payload = {
                    ...chossenWallet,
                    tokenId,
                    amount: amount * 1e11,
                    settings: slippage == 0 ? [{ slippage: [] }] : [{ slippage: [[price, Math.floor((parseFloat(slippage / 1e5)) * 1e5)]] }],
                };

                const response = await fetch(`${ API_BASE_URL }/quicksell`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                });

                const data = await response.json();

                if (!response.ok) {
                    toast.update(toastId, {
                        render: data.error,
                        type: "error",
                        isLoading: false,
                        autoClose: 5000,
                    });
                } else {
                    toast.update(toastId, {
                        render: `Successfully Sold ${ amount } of ${ tokenId }`,
                        type: "success",
                        isLoading: false,
                        autoClose: 5000,
                    });
                }
            } catch (error) {
                console.error("QuickSell error:", error);
                toast.update(toastId, {
                    render: `Error: ${ error.message }`,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                });
            }
        }
    };

    if (!isConnected) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                    p: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${ theme.palette.divider }`,
                    borderRadius: theme.shape.borderRadius,
                }}
            >
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    {t('common.connectWalletAndCreateWallet')}
                </Typography>
            </Box>
        );
    }

    return (
        <Stack
            direction="row"
            spacing={1.5} // Adjust spacing as needed
            alignItems="center"
            justifyContent="center" // Align items to the center
            sx={{
                width: '100%',
                px: 1,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${ theme.palette.divider }`,
                borderRadius: theme.shape.borderRadius,
                py: 1.5,
            }} // Add padding if needed within its container
        >
            {/* Wallet/Group Selector */}
            <FormControl size="small" sx={{ minWidth: 160, flexShrink: 0 }}> {/* Prevent shrinking too much */}
                <InputLabel id="quicksell-wallet-label">{t('common.walletGroup')}</InputLabel>
                <Select
                    labelId="quicksell-wallet-label"
                    label={t('common.walletGroup')}
                    name="wallet"
                    value={selectedWallet}
                    onChange={handleWalletChange}
                    fullWidth
                    displayEmpty
                    renderValue={(selected) => {
                        if (!selected) {
                            return (
                                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                    {t('common.selectWalletGroup')}
                                </Typography>
                            );
                        }
                        const walletObj =
                            favoriteWallets.find((wallet) => wallet._id === selected) ||
                            walletGroups.find((group) => group._id === selected);
                        return walletObj ? walletObj.name : "Unknown Wallet";
                    }}
                >
                    {/* Placeholder Option */}
                    <MenuItem value="" disabled>
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            {t('common.selectWalletGroup')}
                        </Typography>
                    </MenuItem>

                    {/* Favorite Wallets Group */}
                    {favoriteWallets.length > 0 && (
                        <ListSubheader sx={{ color: theme.palette.text.primary }}>
                            {t('common.favorites')}
                        </ListSubheader>
                    )}
                    {favoriteWallets.map((wallet) => (
                        <MenuItem key={wallet._id} value={wallet._id}>
                            <Typography color="text.primary">{wallet.name}</Typography>
                        </MenuItem>
                    ))}

                    {/* Wallet Groups Group */}
                    {walletGroups.length > 0 && (
                        <ListSubheader sx={{ color: theme.palette.text.primary }}>
                            {t('common.groups')}
                        </ListSubheader>
                    )}
                    {walletGroups.map((group) => (
                        <MenuItem key={group._id} value={group._id}>
                            <Typography color="text.primary">{group.name} (Group)</Typography>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <TextField
                label={t('common.amount')}
                size="small"
                variant="outlined"
                value={amount}
                onChange={handleAmountChange}
                sx={{ width: '100px' }} // Control width
                type="text"
                inputMode="decimal"
            />


            <TextField
                label={t('common.slippage')}
                size="small"
                variant="outlined"
                value={slippage}
                onChange={handleSlippageChange}
                type="number" // Fix typo from "mumber" to "number"
                inputMode="decimal"
                InputProps={{
                    // Use InputAdornment for proper styling/alignment
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{
                    maxLength: 5,
                    // Optional: Add min/max validation if needed
                    min: 0,
                    max: 100,
                }} // Limit input length reasonably
                sx={{ width: '90px' }} // Slightly smaller width maybe
            />

            <Button
                variant="contained"
                size="small"
                color="secondary"
                onClick={handleQuickSell}
                sx={{
                    whiteSpace: "nowrap",
                    // Use theme colors for disabled state if needed
                    '&.Mui-disabled': {
                        backgroundColor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                    },
                }}
            >
                {t('common.quickSell')}
            </Button>
        </Stack>
    );
};

export default QuickSell;
